import { datadogLogs } from '@datadog/browser-logs';
import { BUILD_ID, getClientRegion } from 'config/environment';
import { APP_CRASH_KEY } from 'constant';
import { logger } from 'services';
import { isLocalhost } from 'utils/is-localhost';
import { isOrbitsEnvironment } from 'utils/is-nitro-env';
import { initDatadogRumAsync, setDatadogRumUser, shouldInitDatadogRum } from './rum-loader';
const dataDogIgnoredMessages = [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    '<IntlProvider> needs to exist in the component ancestry',
    'db ready with version',
    // React error #188: "Unable to find node on an unmounted component."
    // Thrown by `rc-trigger@5.3.4` (frozen since 2022-11-24) calling
    // `findDOMNode` on antd v4 Tooltip / Popover / Dropdown refs that have
    // already unmounted. Harmless to users — the popover just doesn't get
    // positioned for that one render — but generates ~8.6k Datadog events
    // per day with no actionable signal. The only real fix path is the
    // antd v4 → v5 migration; suppressing the noise until that lands.
    // Match is intentionally narrow ("Minified React error #188") so a
    // genuine #188 from a future code path wouldn't accidentally be
    // captured by a broader pattern — though right now there's no way to
    // distinguish "ours" from "rc-trigger's" given the stack is all
    // inside React internals. Shipped in PR #17635.
    'Minified React error #188',
    // `Cannot read properties of null (reading 'nodeType')` — same
    // `rc-trigger@5.3.4` `findDOMNode` root cause as React #188 above, but
    // a different minified message that doesn't get caught by the #188
    // filter. Frozen library, harmless to users — the popover just doesn't
    // position correctly for one render. Was contributing ~5k events/day
    // to the `status:error` filter.
    "reading 'nodeType'",
    // `Uncaught "Empty reason"` — browser-native unhandled-promise-rejection
    // with no `.reason` payload. The Datadog SDK's `forwardErrorsToLogs`
    // captures these; the source isn't in our code (Pusher / vendor SDK
    // internals). Zero actionable signal — the real error, when there is
    // one, surfaces through a different log path with real context. Was
    // contributing ~11k events/day.
    // Pattern is anchored to the literal quoted form so a backend / form
    // validation message like `Approval rejected: empty reason field`
    // doesn't get silently dropped here.
    '"Empty reason"'
];
const dataDogIgnoredMessagePattern = new RegExp(dataDogIgnoredMessages.join('|'), 'i');
const getDatadogConfig = ()=>{
    const clientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN || '';
    const applicationId = process.env.REACT_APP_DATADOG_APPLICATION_ID || '';
    const site = process.env.REACT_APP_DATADOG_SITE || 'datadoghq.com';
    if (!clientToken) {
        return null;
    }
    return {
        clientToken,
        site,
        applicationId,
        service: 'rocket-frontend',
        version: BUILD_ID
    };
};
/** Browser Logs only — safe for the initial boot bundle. */ export const initDatadog = ()=>{
    if (isLocalhost() || isOrbitsEnvironment()) {
        logger.log('Datadog disabled on localhost');
        return;
    }
    const config = getDatadogConfig();
    if (!config) {
        logger.log('Datadog disabled: credentials not configured');
        return;
    }
    const { applicationId: _applicationId, ...logsConfig } = config;
    datadogLogs.init({
        ...logsConfig,
        sessionSampleRate: 100,
        forwardErrorsToLogs: true,
        forwardConsoleLogs: [
            'error'
        ],
        beforeSend: ({ message, origin })=>{
            if (dataDogIgnoredMessagePattern.test(message)) {
                return false;
            }
            // `forwardConsoleLogs` re-captures React's console.error of Errors we
            // already logged explicitly via logger.error, producing a duplicate
            // line per crash whose only extra content is the webpack-runtime stack.
            // Every such console event has an origin:logger twin (verified against
            // production 2026-07-15), so dropping it loses nothing. Console events
            // WITHOUT the marker (retry-plugin, saga diagnostics) still flow.
            if (origin === 'console' && message.includes(APP_CRASH_KEY)) {
                return false;
            }
            return true;
        }
    });
    datadogLogs.setGlobalContextProperty('build_id', BUILD_ID);
    datadogLogs.setGlobalContextProperty('region', getClientRegion() || 'us');
};
/** Loads RUM in an async chunk — nitro / leadsquared only. */ export const initDatadogRum = ()=>{
    if (!shouldInitDatadogRum()) {
        return;
    }
    const config = getDatadogConfig();
    if (!(config === null || config === void 0 ? void 0 : config.applicationId)) {
        return;
    }
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    initDatadogRumAsync(config).catch(()=>undefined);
};
export const setDataDogIdentity = (userInfo)=>{
    if (!shouldInitDatadogRum()) {
        return;
    }
    setDatadogRumUser(userInfo);
};
export { shouldInitDatadogRum } from './rum-loader';
